// Shared presentation helpers for vacation / leave requests.
// Keeps the manager dashboard and the approval queue visually and
// behaviourally consistent (same colours, icons, status + overlap logic).

import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded';
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ChildFriendlyRoundedIcon from '@mui/icons-material/ChildFriendlyRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import PregnantWomanRoundedIcon from '@mui/icons-material/PregnantWomanRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';

export const LEAVE_TYPE_LABELS = {
  Annual: 'Annual',
  Sick: 'Sick',
  Emergency: 'Emergency',
  Unpaid: 'Unpaid',
  BabyBorn: 'New-Baby',
  FamilyDeath: 'Passing-away (Family)',
  HusbandDeath: 'Passing-away (Husband)',
  Exam: 'Exam',
  Haj: 'Hajj',
  Marriage: 'Marriage',
  Pregnancy: 'Pregnancy',
  Payroll: 'Payroll Correction',
};

// tone === a MUI palette key (success | warning | error | info | secondary | primary)
const LEAVE_TYPE_VISUALS = {
  Annual: { tone: 'success', Icon: BeachAccessRoundedIcon },
  Sick: { tone: 'warning', Icon: LocalHospitalRoundedIcon },
  Emergency: { tone: 'error', Icon: ReportProblemRoundedIcon },
  Unpaid: { tone: 'info', Icon: AccountBalanceWalletRoundedIcon },
  BabyBorn: { tone: 'info', Icon: ChildFriendlyRoundedIcon },
  Pregnancy: { tone: 'info', Icon: PregnantWomanRoundedIcon },
  Exam: { tone: 'info', Icon: SchoolRoundedIcon },
  Marriage: { tone: 'secondary', Icon: FavoriteRoundedIcon },
  Haj: { tone: 'secondary', Icon: TravelExploreRoundedIcon },
  FamilyDeath: { tone: 'secondary', Icon: EventNoteRoundedIcon },
  HusbandDeath: { tone: 'secondary', Icon: EventNoteRoundedIcon },
  Payroll: { tone: 'secondary', Icon: ReceiptLongRoundedIcon },
};

const DEFAULT_VISUAL = { tone: 'secondary', Icon: EventNoteRoundedIcon };

// Leave types arrive either as a raw key (e.g. "Annual") or a decorated
// label (e.g. "Passing-away (Husband)"); strip the parenthetical first.
function baseLeaveKey(leaveType) {
  return String(leaveType || '').replace(/\(.*?\)/g, '').trim();
}

export function getLeaveTypeVisual(leaveType) {
  return LEAVE_TYPE_VISUALS[baseLeaveKey(leaveType)] || DEFAULT_VISUAL;
}

export function getLeaveTypeLabel(leaveType) {
  const key = baseLeaveKey(leaveType);
  return LEAVE_TYPE_LABELS[key] || leaveType || 'Leave';
}

export function normalizeLeaveStatus(status) {
  const value = String(status || '').trim().toLowerCase();
  if (value === 'fully_approved' || value === 'accepted') return 'approved';
  if (value === 'revision_requested') return 'revision';
  if (value === 'declined') return 'rejected';
  if (value === 'canceled') return 'cancelled';
  if (['pending', 'approved', 'rejected', 'revision', 'cancelled'].includes(value)) return value;
  return value || 'pending';
}

export function isPendingStatus(status) {
  return normalizeLeaveStatus(status) === 'pending';
}

// Counts towards "someone is unavailable" — confirmed or awaiting decision,
// but not closed-out rejections / cancellations.
export function blocksCoverage(status) {
  const value = normalizeLeaveStatus(status);
  return value === 'approved' || value === 'pending';
}

function toDayStart(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export function getRequestRange(request) {
  const start = toDayStart(request?.start_date || request?.date_from || request?.start);
  const endRaw = request?.end_date || request?.date_to || request?.end;
  const end = toDayStart(endRaw) || start;
  return { start, end };
}

export function durationInDays(request) {
  const explicit = Number(request?.duration_in_days ?? request?.days ?? request?.duration);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const { start, end } = getRequestRange(request);
  if (!start || !end) return null;
  return Math.max(1, Math.round((end - start) / 86400000) + 1);
}

export function getEmployeeName(request) {
  return request?.user?.name || request?.employee_name || request?.name || 'Unknown';
}

export function getEmployeeCode(request) {
  return request?.user?.employee_code || request?.user_id || request?.employee_code || null;
}

function rangesOverlap(a, b) {
  if (!a.start || !a.end || !b.start || !b.end) return false;
  return a.start <= b.end && b.start <= a.end;
}

// Other teammates whose leave overlaps `target`'s dates (different person,
// status still counts toward coverage). Used to warn a manager before they
// approve a request that would thin out a particular day.
export function findOverlaps(target, pool) {
  const targetRange = getRequestRange(target);
  const targetCode = getEmployeeCode(target);
  const seen = new Map();

  (pool || []).forEach((row) => {
    if (!row || row === target) return;
    if (row.id != null && target.id != null && row.id === target.id) return;
    const code = getEmployeeCode(row);
    if (code && targetCode && code === targetCode) return;
    if (!blocksCoverage(row.status)) return;
    if (!rangesOverlap(targetRange, getRequestRange(row))) return;
    const name = getEmployeeName(row);
    if (!seen.has(name)) {
      seen.set(name, { name, status: normalizeLeaveStatus(row.status), ...getRequestRange(row) });
    }
  });

  return Array.from(seen.values());
}

function startOfWeekMonday(refDate) {
  const base = toDayStart(refDate) || toDayStart(new Date());
  const day = base.getDay(); // 0 = Sun
  const diff = (day + 6) % 7; // days since Monday
  return new Date(base.getFullYear(), base.getMonth(), base.getDate() - diff);
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Confirmed-coverage view of the current week: for each day, how many of the
// team are available (teamSize minus distinct people on approved leave).
export function computeTeamWeek(requests, teamSize, refDate) {
  const weekStart = startOfWeekMonday(refDate);
  const approved = (requests || []).filter((row) => normalizeLeaveStatus(row?.status) === 'approved');

  const days = WEEKDAY_LABELS.map((label, index) => {
    const date = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + index);
    const dayRange = { start: date, end: date };
    const offCodes = new Set();
    approved.forEach((row, rowIndex) => {
      if (rangesOverlap(dayRange, getRequestRange(row))) {
        offCodes.add(getEmployeeCode(row) || `row-${rowIndex}`);
      }
    });
    const off = offCodes.size;
    const available = Math.max(0, Number(teamSize || 0) - off);
    return { key: label, label, date, dateLabel: date.getDate(), off, available };
  });

  const minAvailable = days.reduce((min, day) => Math.min(min, day.available), Number.POSITIVE_INFINITY);
  const anyOff = days.some((day) => day.off > 0);
  return days.map((day) => ({
    ...day,
    isTightest: anyOff && day.available === minAvailable,
  }));
}
